import { InfoMeetingAttendeeDoNotForward } from 'owa-locstrings/lib/strings/infomeetingattendeedonotforward.locstring.json';
import {
    groupMemberWillContinueToReceiveUpdatesInInbox,
    ErrorCalendarEventCanNotBeCreated,
    ErrorCannotCreatePublicFolderMeetings,
    InfoEditingMasterOfSeries,
    InfoEditingThisAndAllFollowingEvents,
    InfoEditingOccurrenceOfSeries,
    ErrorInvalidRecipientsMeetingCanNotExecuteOperation,
    InfoMultipleAttendeesHaveProposedNewTime,
    InfoAttendeeDeclinedAndProposedNewTime,
    InfoAttendeeTentativeAndProposedNewTime,
    InfoDoesNotRequireResponseAttendee,
    InfoMeetingInvitationNotSent,
    InfoMeetingUpdatesNotSent,
    WarningCannotEditPublicFolderMeetings,
    WarningNoResponseTrackingForMeetingOnSecondaryCalendar,
    eventSeriesOutOfRoomBookingWindow,
    eventUpdateRecurrenceEndDate,
    infobarAcceptedByDelegate,
    infobarTentativelyAcceptedByDelegate,
    infobarDeclinedByDelegate,
    infobarEventSafetyMessageBlockedExternalContent,
    infobarEventShowBlockedExternalContent,
} from './allViewStates.locstring.json';
import { sendEventToAllGroupMembers } from 'owa-locstrings/lib/strings/sendeventtoallgroupmembers.locstring.json';
import { invitationWontBeSentToGroup } from 'owa-locstrings/lib/strings/invitationwontbesenttogroup.locstring.json';
import {
    groupMemberWillReceiveInvite,
    groupMemberWillGetMeetingCancellation,
} from '../strings.locstring.json';
import { InfoComposeTeamsMeetingDetails } from 'owa-locstrings/lib/strings/infocomposeteamsmeetingdetails.locstring.json';
import { InfoComposeSkypeMeetingDetails } from 'owa-locstrings/lib/strings/infocomposeskypemeetingdetails.locstring.json';
import { InfoTimeProposalCurrentMeetingTime } from 'owa-locstrings/lib/strings/infotimeproposalcurrentmeetingtime.locstring.json';
import { errorMessageNoRecipentsMailCanNotBeSend } from 'owa-locstrings/lib/strings/errormessagenorecipentsmailcannotbesend.locstring.json';
import loc, { format } from 'owa-localize';
import AttendeeType from 'owa-service/lib/contract/AttendeeType';

import formatMeetingTimeSpan from 'owa-timeformat/lib/utils/formatMeetingTimeSpan';

import ResponseTypeType from 'owa-service/lib/contract/ResponseTypeType';
import { OwaDate, formatUserTime, formatWeekDayDate, owaDate, userDate } from 'owa-datetime';

import {
    InfoBarCustomAction,
    InfoBarMessageColor,
    InfoBarMessageRank,
    InfoBarMessageSource,
    InfoBarMessageViewState,
} from 'owa-info-bar';

export function getOnlineMeetingInfoBarMessage(isSkype: boolean): string {
    return isSkype ? loc(InfoComposeSkypeMeetingDetails) : loc(InfoComposeTeamsMeetingDetails);
}

/**
 * Retuns the string about the group members geting invitaion or updates for an event on group calendar. If the event is a new Meeting
 * it returns "Group members will receive an invite ...", otherwise it returns "Group members will continue to receive ..."
 */
export function getGroupMemberNotificationInfoBarMessage(
    isGroupAnAttendee: Boolean,
    isGroupOriginalAttendee: Boolean
) {
    if (isGroupAnAttendee && isGroupOriginalAttendee) {
        return loc(groupMemberWillContinueToReceiveUpdatesInInbox);
    }

    if (isGroupOriginalAttendee) {
        return loc(groupMemberWillGetMeetingCancellation);
    }
    return loc(groupMemberWillReceiveInvite);
}

export interface GetInfoBarViewStatesProps {
    showBlockedExternalContent?: () => void;
    newTimeProposers?: AttendeeType[];
    onSendEventToAllGroupMembersClicked?: () => void;
    onChangeEndDateClicked?: () => void;
    isGroupAnAttendee?: boolean;
    isGroupOriginalAttendee?: boolean;
    appointmentReplyName?: string;
    appointmentReplyDate?: OwaDate;
    appointmentResponseType?: ResponseTypeType;
    source?: InfoBarMessageSource;
    timeZoneId?: string;
    newTimeCustomAction?: InfoBarCustomAction;
}

// Dictionary of InfoBarMessageViewState generators
export const InfoBarViewStates: {
    [key: string]: (options?: GetInfoBarViewStatesProps) => InfoBarMessageViewState;
} = {
    // 'Skype/Teams meeting details will be added after you send the invitation.'
    getInfoComposeOnlineMeetingDetails(): InfoBarMessageViewState {
        return {
            key: 'OnlineMeetingToast',
            message: getOnlineMeetingInfoBarMessage(true),
            messageParts: [getOnlineMeetingInfoBarMessage(true)],
            rank: InfoBarMessageRank.Info,
            source: InfoBarMessageSource.Compose,
        };
    },

    // 'This message must have at least one recipient.'
    getErrorMessageNoRecipentsMailCanNotBeSend(): InfoBarMessageViewState {
        return {
            key: 'hasForwardeesLengthError',
            message: loc(errorMessageNoRecipentsMailCanNotBeSend),
            messageParts: [loc(errorMessageNoRecipentsMailCanNotBeSend)],
            rank: InfoBarMessageRank.Info,
            source: InfoBarMessageSource.Compose,
        };
    },

    // Instant calendar event creation failed to commit event onto the server
    getErrorCalendarEventCanNotBeCreated(): InfoBarMessageViewState {
        return {
            key: 'ErrorCalendarEventCanNotBeCreated',
            message: loc(ErrorCalendarEventCanNotBeCreated),
            messageParts: [loc(ErrorCalendarEventCanNotBeCreated)],
            rank: InfoBarMessageRank.Info,
            source: InfoBarMessageSource.Compose,
            color: InfoBarMessageColor.Red,
        };
    },

    // Info bar message shown to users when they attempt to create a public calendar folder meeting request
    getErrorCannotCreatePublicFolderMeetings(): InfoBarMessageViewState {
        return {
            key: 'ErrorCannotCreatePublicFolderMeetings',
            message: loc(ErrorCannotCreatePublicFolderMeetings),
            messageParts: [loc(ErrorCannotCreatePublicFolderMeetings)],
            rank: InfoBarMessageRank.Error,
            source: InfoBarMessageSource.Compose,
            color: InfoBarMessageColor.Red,
        };
    },

    // External content is blocked in this event
    getInfobarViewStateForBlockedExternalContent(
        options: GetInfoBarViewStatesProps
    ): InfoBarMessageViewState {
        const messageText = loc(infobarEventSafetyMessageBlockedExternalContent);
        return {
            key: 'InfoBlockedExternalContent',
            message: messageText,
            messageParts: [
                messageText,
                {
                    text: loc(infobarEventShowBlockedExternalContent),
                    action: options.showBlockedExternalContent,
                },
            ],
            rank: InfoBarMessageRank.Safety,
            source: options.source,
            color: InfoBarMessageColor.Grey,
        };
    },

    // Occurrence is master of series
    getInfoEditingMasterOfSeries(): InfoBarMessageViewState {
        return {
            key: 'InfoEditingMasterOfSeries',
            message: loc(InfoEditingMasterOfSeries),
            messageParts: [loc(InfoEditingMasterOfSeries)],
            rank: InfoBarMessageRank.Info,
            source: InfoBarMessageSource.Compose,
        };
    },

    // Occurrence is "from this day forward" master of series
    getInfoEditingFromThisDayMasterOfSeries(): InfoBarMessageViewState {
        return {
            key: 'InfoEditingThisAndAllFollowingEvents',
            message: loc(InfoEditingThisAndAllFollowingEvents),
            messageParts: [loc(InfoEditingThisAndAllFollowingEvents)],
            rank: InfoBarMessageRank.Info,
            source: InfoBarMessageSource.Compose,
        };
    },

    // Occurrence is part of series
    getInfoEditingOccurrenceOfSeries(): InfoBarMessageViewState {
        return {
            key: 'InfoEditingOccurrenceOfSeries',
            message: loc(InfoEditingOccurrenceOfSeries),
            messageParts: [loc(InfoEditingOccurrenceOfSeries)],
            rank: InfoBarMessageRank.Info,
            source: InfoBarMessageSource.Compose,
        };
    },

    // Error message for meeting compose when there are invalid attendees
    getErrorInvalidRecipientsMeetingCanNotExecuteOperation(): InfoBarMessageViewState {
        return {
            key: 'ErrorInvalidRecipientsMeetingCanNotExecuteOperation',
            message: loc(ErrorInvalidRecipientsMeetingCanNotExecuteOperation),
            messageParts: [loc(ErrorInvalidRecipientsMeetingCanNotExecuteOperation)],
            rank: InfoBarMessageRank.Info,
            source: InfoBarMessageSource.Compose,
        };
    },

    // Multiple attendees have proposed a new meeting time
    getInfoMultipleAttendeesHaveProposedNewTime(
        options: GetInfoBarViewStatesProps
    ): InfoBarMessageViewState {
        return {
            key: 'InfoMultipleAttendeesHaveProposedNewTime',
            message: loc(InfoMultipleAttendeesHaveProposedNewTime),
            messageParts: [loc(InfoMultipleAttendeesHaveProposedNewTime)],
            rank: InfoBarMessageRank.Safety,
            color: InfoBarMessageColor.Grey,
            source: options.source,
        };
    },

    // One user has proposed a new time to meet
    getInfoAttendeeHasProposedNewTimeLegacy({
        newTimeProposers,
        source,
        timeZoneId,
    }: GetInfoBarViewStatesProps): InfoBarMessageViewState {
        if (!newTimeProposers || newTimeProposers.length == 0) {
            return null;
        }

        const attendee = newTimeProposers[0];
        const message =
            attendee.ResponseType == 'Decline'
                ? loc(InfoAttendeeDeclinedAndProposedNewTime)
                : loc(InfoAttendeeTentativeAndProposedNewTime);

        const start = timeZoneId
            ? owaDate(timeZoneId, attendee.ProposedStart)
            : userDate(attendee.ProposedStart);
        const end = timeZoneId
            ? owaDate(timeZoneId, attendee.ProposedEnd)
            : userDate(attendee.ProposedEnd);

        const proposedNewTimeString = format(
            message,
            attendee.Mailbox.Name,
            formatMeetingTimeSpan(start, end)
        );

        return {
            key: 'InfoSingleAttendeesHaveProposedNewTime',
            message: proposedNewTimeString,
            messageParts: attendee ? [proposedNewTimeString] : [],
            rank: InfoBarMessageRank.Safety,
            color: InfoBarMessageColor.Grey,
            source: source,
        };
    },

    getInfobarViewStateForCurrentTime(options: GetInfoBarViewStatesProps): InfoBarMessageViewState {
        if (!options.newTimeCustomAction) {
            return null;
        }

        return {
            key: 'infoCurrentMeetingTime',
            message: loc(InfoTimeProposalCurrentMeetingTime, options.newTimeCustomAction.text),
            localizedMessageTemplateString: loc(InfoTimeProposalCurrentMeetingTime),
            messageParts: [options.newTimeCustomAction],
            rank: InfoBarMessageRank.Info,
            source: options.source,
        };
    },

    getInfobarViewStateForSingleProposedNewTime(
        options: GetInfoBarViewStatesProps
    ): InfoBarMessageViewState {
        if (!options.newTimeCustomAction) {
            return null;
        }

        const { newTimeProposers } = options;
        const attendee = newTimeProposers[0];
        const templateString =
            attendee.ResponseType == 'Decline'
                ? InfoAttendeeDeclinedAndProposedNewTime
                : InfoAttendeeTentativeAndProposedNewTime;
        return {
            key: 'infoSingleProposedNewTime',
            message: loc(templateString, attendee.Mailbox.Name, options.newTimeCustomAction.text),
            localizedMessageTemplateString: loc(templateString),
            messageParts: [attendee.Mailbox.Name, options.newTimeCustomAction],
            rank: InfoBarMessageRank.Safety,
            color: InfoBarMessageColor.Orange,
            source: options.source,
        };
    },

    // The organizer hasn't requested a response for this event. To update your own calendar, select a response.
    getInfoDoesNotRequireResponseAttendee(
        options: GetInfoBarViewStatesProps
    ): InfoBarMessageViewState {
        return {
            key: 'InfoDoesNotRequireResponseAttendee',
            message: loc(InfoDoesNotRequireResponseAttendee),
            messageParts: [loc(InfoDoesNotRequireResponseAttendee)],
            rank: InfoBarMessageRank.Info,
            source: InfoBarMessageSource.Compose,
        } as InfoBarMessageViewState;
    },

    // The meeting organizer has disabled forwarding for this invitation.
    getInfoMeetingAttendeeDoNotForward(
        options: GetInfoBarViewStatesProps
    ): InfoBarMessageViewState {
        return {
            key: 'InfoMeetingAttendeeDoNotForward',
            message: loc(InfoMeetingAttendeeDoNotForward),
            messageParts: [loc(InfoMeetingAttendeeDoNotForward)],
            rank: InfoBarMessageRank.Info,
            source: InfoBarMessageSource.Compose,
        } as InfoBarMessageViewState;
    },

    // The user is editing a draft meeting which has not been sent.
    getInfoMeetingInvitationNotSent(options: GetInfoBarViewStatesProps) {
        return {
            key: 'InfoMeetingInvitationNotSent',
            message: loc(InfoMeetingInvitationNotSent),
            messageParts: [loc(InfoMeetingInvitationNotSent)],
            rank: InfoBarMessageRank.Info,
            source: InfoBarMessageSource.Compose,
        } as InfoBarMessageViewState;
    },

    // // The user is editing updates to a draft meeting which have not been sent
    getInfoMeetingUpdatesNotSent(options: GetInfoBarViewStatesProps): InfoBarMessageViewState {
        return {
            key: 'InfoMeetingUpdatesNotSent',
            message: loc(InfoMeetingUpdatesNotSent),
            messageParts: [loc(InfoMeetingUpdatesNotSent)],
            rank: InfoBarMessageRank.Info,
            source: InfoBarMessageSource.Compose,
        };
    },

    // Public folder meetings cannot be modified in OWA.
    getWarningCannotEditPublicFolderMeetings(
        options: GetInfoBarViewStatesProps
    ): InfoBarMessageViewState {
        return {
            key: 'WarningCannotEditPublicFolderMeetings',
            message: loc(WarningCannotEditPublicFolderMeetings),
            messageParts: [loc(WarningCannotEditPublicFolderMeetings)],
            rank: InfoBarMessageRank.Safety,
            source: InfoBarMessageSource.Compose,
            color: InfoBarMessageColor.Yellow,
        };
    },

    // 'Responses won't be tallied for events saved on this calendar. To tally responses, use your primary calendar.'
    getWarningNoResponseTrackingForMeetingOnSecondaryCalendar(
        options: GetInfoBarViewStatesProps
    ): InfoBarMessageViewState {
        return {
            key: 'WarningNoResponseTrackingForMeetingOnSecondaryCalendar',
            message: loc(WarningNoResponseTrackingForMeetingOnSecondaryCalendar),
            messageParts: [loc(WarningNoResponseTrackingForMeetingOnSecondaryCalendar)],
            rank: InfoBarMessageRank.Safety,
            source: InfoBarMessageSource.Compose,
            color: InfoBarMessageColor.Yellow,
            ariaProps: {
                role: 'alert',
                live: 'assertive',
            },
        } as InfoBarMessageViewState;
    },

    getSendEventToAllGroupMembers(options: GetInfoBarViewStatesProps): InfoBarMessageViewState {
        if (!options.onSendEventToAllGroupMembersClicked) {
            return null;
        }

        const invitiationWontBeSentString = loc(invitationWontBeSentToGroup);

        return {
            key: 'SendEventToAllGroupMembers',
            message: invitiationWontBeSentString,
            messageParts: [
                invitiationWontBeSentString,
                {
                    text: loc(sendEventToAllGroupMembers),
                    action: () => {
                        options.onSendEventToAllGroupMembersClicked();
                    },
                } as InfoBarCustomAction,
            ],
            rank: InfoBarMessageRank.Info,
            source: InfoBarMessageSource.Compose,
        };
    },

    getOutOfRoomBookingWindowPolicy(options: GetInfoBarViewStatesProps): InfoBarMessageViewState {
        if (!options.onChangeEndDateClicked) {
            return null;
        }

        const eventOutOfSeriesString = loc(eventSeriesOutOfRoomBookingWindow);

        return {
            key: 'ChangeRecurrenceEndDate',
            message: eventOutOfSeriesString,
            messageParts: [
                eventOutOfSeriesString,
                {
                    text: loc(eventUpdateRecurrenceEndDate),
                    action: () => {
                        options.onChangeEndDateClicked();
                    },
                } as InfoBarCustomAction,
            ],
            rank: InfoBarMessageRank.Info,
            source: InfoBarMessageSource.Compose,
        };
    },

    getEventWillBeSentToAllGroupMembers(
        options: GetInfoBarViewStatesProps
    ): InfoBarMessageViewState {
        const allGroupsString = getGroupMemberNotificationInfoBarMessage(
            options.isGroupAnAttendee,
            options.isGroupOriginalAttendee
        );

        return {
            key: 'EventWillBeSentToAllGroupMembers',
            message: allGroupsString,
            messageParts: [allGroupsString],
            rank: InfoBarMessageRank.Safety,
            source: InfoBarMessageSource.Compose,
        };
    },

    // Delegate has responded to the calendar event
    getMeetingResponseByDelegate(options: GetInfoBarViewStatesProps): InfoBarMessageViewState {
        let message = null;
        switch (options.appointmentResponseType) {
            case 'Accept':
                message = loc(infobarAcceptedByDelegate);
                break;
            case 'Tentative':
                message = loc(infobarTentativelyAcceptedByDelegate);
                break;
            case 'Decline':
                message = loc(infobarDeclinedByDelegate);
                break;
        }
        message = message
            ? format(
                  message,
                  options.appointmentReplyName,
                  formatWeekDayDate(options.appointmentReplyDate),
                  formatUserTime(options.appointmentReplyDate)
              )
            : null;
        return {
            key: 'InfoMeetingResponseByDelegate',
            message,
            messageParts: message ? [message] : [],
            rank: InfoBarMessageRank.Info,
            source: InfoBarMessageSource.Compose,
        };
    },
};

export default InfoBarViewStates;
